import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import type { Supplier, UncostedItem } from '../api/types'
import { formatMoney, formatQty, toNumber } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Select, Spinner, TextInput,
} from '@shop/ui'

/** ما يُرسَل ويعود — مطابقٌ لـ`CostInvoiceDetail` في الخادم. */
type CostInvoice = {
  header: {
    id: string
    invoiceNo: string
    status: string
    supplierName: string
    total: number
    stockDelta: number
  }
}

/** سطرٌ مختارٌ على الشاشة — والكمّيّةُ والكلفةُ نصٌّ حتّى يُرسَل. */
type Picked = { qty: string; cost: string }

/**
 * فاتورةُ المشتريات — <b>البضاعةُ وصلت وبيعت، والكلفةُ تأتي بعدها</b>.
 *
 * <p>
 * ⚠ <b>قرارُ المالك ٢٠٢٦-٠٩-٢٨:</b> الموظّفُ يستلم فتنزل الكمّيّةُ فورًا
 * ويُباع الصنفُ في الحال. وهنا تختار ما دخل بلا كلفةٍ — ولو من أيّامٍ
 * مختلفة — فتربطه بمورّدٍ وتضع الكلف، <b>وعند الترحيل تنزل الكلفةُ والذمّة</b>.
 * </p>
 *
 * <p>
 * ⚠ <b>والكمّيّةُ لا تتحرّك عند الترحيل</b> — تُضاف قيمةٌ فقط. فالقائمةُ هنا
 * لا تُدخل بضاعةً، بل تُسعّر بضاعةً دخلت.
 * </p>
 */
export default function PurchaseInvoice() {
  const { session, can } = useAuth()
  const queries = useQueryClient()

  const [supplierId, setSupplierId] = useState('')
  const [invoiceNo, setInvoiceNo] = useState('')
  const [invoiceDate, setInvoiceDate] = useState('')
  const [picked, setPicked] = useState<Record<string, Picked>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [done, setDone] = useState<CostInvoice | null>(null)

  const suppliers = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api<Supplier[]>('/api/v1/suppliers'),
  })

  // ⚠ **والقائمةُ هي «ما على الرفّ بلا كلفة» نفسُها** — لا قائمةٌ ثانية.
  //   فما استلمه الموظّفُ يظهر هنا وحدَه، ويختفي حين تُرحَّل فاتورتُه.
  const items = useQuery({
    queryKey: ['uncosted'],
    queryFn: () => api<UncostedItem[]>('/api/v1/inventory/uncosted?limit=500'),
    enabled: can(Perm.costView),
  })

  const branchId = session?.me.activeBranchId ?? ''
  const list = items.data ?? []

  const key = (item: UncostedItem) => `${item.variantId}:${item.warehouseId}`

  const total = useMemo(
    () => Object.values(picked).reduce(
      (sum, p) => sum + (toNumber(p.qty) ?? 0) * (toNumber(p.cost) ?? 0), 0),
    [picked],
  )

  const chosen = Object.entries(picked)
  const missingCost = chosen.some(([, p]) => (toNumber(p.cost) ?? -1) < 0 || p.cost.trim() === '')

  const toggle = (item: UncostedItem) => {
    setPicked((current) => {
      const next = { ...current }

      if (next[key(item)]) {
        delete next[key(item)]
      } else {
        // والكمّيّةُ ما على الرفّ افتراضًا — وتُنقَص إن كان بعضُها لمورّدٍ آخر.
        next[key(item)] = { qty: String(item.onHand), cost: '' }
      }

      return next
    })
  }

  const set = (item: UncostedItem, patch: Partial<Picked>) => {
    setPicked((current) => ({ ...current, [key(item)]: { ...current[key(item)], ...patch } }))
  }

  const post = async () => {
    setBusy(true)
    setError(null)

    try {
      const invoice = await api<CostInvoice>('/api/v1/cost-invoices', {
        method: 'POST',
        body: {
          supplierId,
          branchId,
          supplierInvoiceNo: invoiceNo.trim() || null,
          supplierInvoiceDate: invoiceDate.trim() || null,
        },
      })

      await api(`/api/v1/cost-invoices/${invoice.header.id}/lines`, {
        method: 'PUT',
        body: {
          lines: chosen.map(([k, p]) => {
            const [variantId, warehouseId] = k.split(':')

            return {
              variantId,
              warehouseId,
              qty: toNumber(p.qty) ?? 0,
              unitCost: toNumber(p.cost) ?? 0,
            }
          }),
        },
      })

      const posted = await api<CostInvoice>(`/api/v1/cost-invoices/${invoice.header.id}/post`, {
        method: 'POST',
        body: {},
      })

      setDone(posted)
      setPicked({})
      setInvoiceNo('')
      setInvoiceDate('')

      await queries.invalidateQueries({ queryKey: ['uncosted'] })
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  if (!can(Perm.costView) || !can(Perm.receiptPost)) {
    return (
      <Panel title="فاتورةُ مشتريات">
        <Banner tone="warn">
          هذه الشاشةُ تضع الكلفَ وتُنزل ذمّةَ المورّد، وتحتاج صلاحيّتَي «رؤيةُ الكلفة»
          و«ترحيلُ الاستلام».
        </Banner>
      </Panel>
    )
  }

  return (
    <Panel title="فاتورةُ مشتريات">
      <Banner tone="info">
        اختر ما استلمه الموظّفون بلا كلفة، واربطه بالمورّد وضع الكلف.
        <b> عند الترحيل تنزل الكلفةُ وذمّةُ المورّد — والكمّيّةُ لا تتحرّك</b>،
        فقد نزلت يومَ الاستلام.
      </Banner>

      {done && (
        <div className="mt-3">
          <Banner tone="ok">
            رُحّلت <b className="tabular" dir="ltr">{done.header.invoiceNo}</b> لـ
            <b>{done.header.supplierName}</b> بـ<b>{formatMoney(done.header.total)}</b> —
            ونزلت ذمّتُه بالمبلغ نفسِه.
          </Banner>
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <Field label="المورّد">
          <Select value={supplierId} onChange={setSupplierId}>
            <option value="">— اختر —</option>
            {(suppliers.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.nameAr}</option>
            ))}
          </Select>
        </Field>

        <Field label="رقمُ فاتورة المورّد" hint="كما هو على الورقة">
          <TextInput value={invoiceNo} onChange={setInvoiceNo} placeholder="اختياريّ" />
        </Field>

        <Field label="تاريخُها">
          <TextInput value={invoiceDate} onChange={setInvoiceDate} placeholder="2026-09-28" />
        </Field>
      </div>

      <ErrorBanner error={error ?? items.error ?? suppliers.error} />

      {items.isPending ? (
        <div className="mt-4"><Spinner label="يُقرأ ما دخل بلا كلفة…" /></div>
      ) : list.length === 0 ? (
        <div className="mt-4"><Empty>لا شيءَ على الرفّ بلا كلفة — كلُّ ما استُلم فُوتر.</Empty></div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-ink-soft">
              <tr className="border-b border-line">
                <th className="p-2" />
                <th className="p-2 text-right">الصنف</th>
                <th className="p-2 text-left">على الرفّ</th>
                <th className="p-2 text-left">الكمّيّة</th>
                <th className="p-2 text-left">كلفةُ الوحدة</th>
                <th className="p-2 text-left">المجموع</th>
              </tr>
            </thead>

            <tbody>
              {list.map((item) => {
                const p = picked[key(item)]
                const line = p ? (toNumber(p.qty) ?? 0) * (toNumber(p.cost) ?? 0) : 0

                return (
                  <tr key={key(item)} className={`border-b border-line/50 ${p ? 'bg-brand/5' : ''}`}>
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={Boolean(p)}
                        onChange={() => toggle(item)}
                        aria-label={`اختر ${item.productName}`}
                      />
                    </td>
                    <td className="p-2">
                      <div className="font-semibold">{item.productName}</div>
                      <div className="tabular text-xs text-ink-soft" dir="ltr">{item.sku}</div>
                    </td>
                    <td className="tabular p-2 text-left" dir="ltr">{formatQty(item.onHand)}</td>
                    <td className="w-28 p-2">
                      {p && <NumberInput value={p.qty} onChange={(qty) => set(item, { qty })} />}
                    </td>
                    <td className="w-32 p-2">
                      {p && (
                        <NumberInput
                          value={p.cost}
                          onChange={(cost) => set(item, { cost })}
                          placeholder="0.000"
                        />
                      )}
                    </td>
                    <td className="tabular p-2 text-left font-bold" dir="ltr">
                      {p ? formatMoney(line) : ''}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="sticky bottom-0 mt-4 flex items-center justify-between gap-3 border-t border-line bg-surface py-3">
        <div>
          <div className="text-xs text-ink-soft">مجموعُ الفاتورة · {chosen.length} سطرًا</div>
          <div className="tabular text-2xl font-bold" dir="ltr">{formatMoney(total)}</div>
        </div>

        <Button
          tone="primary"
          disabled={busy || supplierId === '' || chosen.length === 0 || missingCost || branchId === ''}
          onClick={() => void post()}
        >
          {busy ? 'يُرحَّل…' : 'رحّل الفاتورة'}
        </Button>
      </div>

      {chosen.length > 0 && missingCost && (
        <p className="text-xs text-warn">اكتب كلفةَ كلِّ سطرٍ مختار — ولو صفرًا إن كان هديّة.</p>
      )}
    </Panel>
  )
}
